/**
 * A photo sphere picked in another app (e.g. the Asset Editor): the dataset
 * folder and point cloud (chunk) it belongs to, plus its position. We resolve
 * it to the nearest photo sphere of that point cloud. An optional point to
 * look at turns the view toward it (e.g. the sign being reviewed).
 */
export interface PhotoSphereTarget {
    /** Dataset folder on the lidar CDN, e.g. `losangeles_ca_processed` */
    dir: string;
    /** Point cloud folder inside `dir`, e.g. `11112025A_6440_015-026` */
    chunk: string;
    lat: number;
    lng: number;
    /** Where the view should face once the sphere is open */
    look?: { lat: number, lng: number };
    /**
     * What the look-at point is, shown on its map marker. Parts separated by
     * ` · ` go on their own lines, e.g. `Sign 1184876 · Signpost 52345`.
     */
    label?: string;
}

const NUMBER = '(-?\\d+\\.?\\d*)';
const QUERY = new RegExp(
    `^\\s*([^\\s/]+)\\/([^\\s/]+)\\s*@\\s*${NUMBER}\\s*,\\s*${NUMBER}(?:\\s*>\\s*${NUMBER}\\s*,\\s*${NUMBER})?(?:\\s*\\|\\s*(.*?))?\\s*$`,
);

/**
 * @description Parses the text Asset Editor copies for a photo sphere,
 * `<dir>/<chunk> @ <lat>,<lng>`, optionally followed by `> <lat>,<lng>` for
 * the point to look at, and then `| <label>` naming that point. The same text is used in the search bar and as the
 * `ae` URL param. A whole Doppel link carrying `ae` is accepted too.
 * @param {string} query The search bar text or `ae` param value
 * @return {PhotoSphereTarget | null} The target, or null if the query isn't one
 */
export function parsePhotoSphereQuery(query: string): PhotoSphereTarget | null {
    const match = query.match(QUERY);
    if (match) {
        const lat = Number(match[3]);
        const lng = Number(match[4]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const target: PhotoSphereTarget = { dir: match[1], chunk: match[2], lat, lng };
        if (match[5] !== undefined) {
            const look = { lat: Number(match[5]), lng: Number(match[6]) };
            if (Number.isFinite(look.lat) && Number.isFinite(look.lng)) target.look = look;
        }
        if (match[7]) target.label = match[7];
        return target;
    }

    if (query.includes('ae=')) {
        try {
            const ae = new URL(query.trim()).searchParams.get('ae');
            return ae ? parsePhotoSphereQuery(ae) : null;
        } catch (e) {
            return null;
        }
    }

    return null;
}

/**
 * @description The query text for a target, the inverse of parsePhotoSphereQuery.
 * Used to put a consumed `ae` param back into the URL before signing in again.
 * @param {PhotoSphereTarget} target The target
 * @return {string} `<dir>/<chunk> @ <lat>,<lng>[ > <lat>,<lng>][ | <label>]`
 */
export function formatPhotoSphereQuery(target: PhotoSphereTarget): string {
    const look = target.look ? ` > ${target.look.lat},${target.look.lng}` : '';
    const label = target.label ? ` | ${target.label}` : '';
    return `${target.dir}/${target.chunk} @ ${target.lat},${target.lng}${look}${label}`;
}
